import { ScrollableArea } from '@approvalmax/ui';
import { Box, ConditionWrapper, Flex, Grid } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { font } from 'modules/styles';
import { FC, memo, useCallback, useEffect } from 'react';
import { useUserBetaFeatures } from 'shared/hooks';
import { ocrIsLoadingState } from 'shared/states';
import styled from 'styled-components';

import { changeQbooksMemo, updateQBooksFormWithContext, uploadAttachments } from '../../../actions';
import { AttachmentsSection, MemoSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import SendToSupplierFileDropArea from '../../../components/SendToSupplierFileDropArea';
import { AttachmentKind } from '../../../data/AttachmentKind';
import useShowDropArea from '../../../hooks/useShowDropArea';
import { getContext } from '../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../selectors/qbooks';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { QBooksAccountLineItemsSection as QBooksAccountLineItemsSectionLegacy } from '../(legacy)/QBooksAccountLineItemsSection/QBooksAccountLineItemsSection';
import { QBooksFooterLegacy } from '../(legacy)/QBooksFooterLegacy/QBooksFooterLegacy';
import { PurchaseOrderShippingDetailsSection } from '../PurchaseOrderShippingDetailsSection/PurchaseOrderShippingDetailsSection';
import { QBooksAccountLineItemsSection } from '../QBooksAccountLineItemsSection/QBooksAccountLineItemsSection';
import QBooksEmailToVendorSection from '../QBooksEmailToVendorSection/QBooksEmailToVendorSection';
import { QBooksLineItemsSection } from '../QBooksLineItemsSection/QBooksLineItemsSection';
import { QBooksRequestItemsSummary } from '../QBooksRequestItemsSummary/QBooksRequestItemsSummary';
import { QBooksVendorMessageSection } from '../QBooksVendorMessageSection/QBooksVendorMessageSection';
import { QBooksVendorSection } from '../QBooksVendorSection/QBooksVendorSection';
import QBooksShippingSection from './components/QBooksShippingSection/QBooksShippingSection';
import { messages } from './QBooksPurchaseOrderForm.messages';

const { qBooksConstants } = constants;

const Root = styled.div`
    display: flex;
    flex-flow: column;
    position: relative;
`;

const Toolbar = styled.div`
    flex: none;
    padding: 25px 60px 30px 60px;
    border-bottom: 1px solid #cacccd;
`;

const ToolbarHeader = styled.div`
    display: flex;
    align-items: center;
`;

const ToolbarHeaderText = styled.div`
    flex: 1;
    text-transform: uppercase;

    ${font(20, '#333', 'semibold')}
`;

const Content = styled(ScrollableArea)`
    flex: 1;
    overflow: hidden;
`;

const InnerContent = styled.div`
    padding-bottom: 40px;
    display: flex;
    flex-flow: column;
`;

interface QBooksPurchaseOrderFormProps {
    className?: string;
    request: domain.QBooksPoRequest;
}

const placeholderList = [
    domain.SupplierEmailReplacements.poNumber,
    domain.SupplierEmailReplacements.companyName,
    domain.SupplierEmailReplacements.totalAmount,
    domain.SupplierEmailReplacements.supplierName,
    domain.SupplierEmailReplacements.contactPersonName,
];

const QBooksPurchaseOrderForm: FC<QBooksPurchaseOrderFormProps> = (props) => {
    const { className, request } = props;

    const { TemplateSettingsQBooksEnabledLineItemType, TemplateSettingsEmailToSupplierState } = domain;

    const dispatch = useDispatch();

    const context = useSelector(getContext);
    const qbooksContext = useSelector(getQBooksContext);
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);

    const showDropArea = useShowDropArea();
    const { isBelgradeUIVendorMessageSection } = useUserBetaFeatures();

    const details = request.details;

    const qbooksEnabledLineItemType = qbooksContext.qbooksEnabledLineItemType;
    const supplierEmailSettings = qbooksContext.supplierEmailSettings;

    const isAccountBasedLinesEnabled =
        qbooksEnabledLineItemType === TemplateSettingsQBooksEnabledLineItemType.All ||
        qbooksEnabledLineItemType === TemplateSettingsQBooksEnabledLineItemType.AccountBasedLines;

    const isItemBasedLinesEnabled =
        qbooksEnabledLineItemType === TemplateSettingsQBooksEnabledLineItemType.All ||
        qbooksEnabledLineItemType === TemplateSettingsQBooksEnabledLineItemType.ItemBasedLines;

    const isEmailToVendorEnabled = supplierEmailSettings?.state !== TemplateSettingsEmailToSupplierState.Disabled;

    const isEmailToVendorReadonly =
        details.emailToSupplier !== null &&
        details.emailToSupplier.emailHasToBeSent &&
        supplierEmailSettings?.state === TemplateSettingsEmailToSupplierState.Disabled;

    const sendToSupplier = details.sendToSupplier;

    const onUploadAttachments = useCallback(
        (files: File[], attachmentKind: AttachmentKind) => {
            dispatch(uploadAttachments({ files, attachmentKind }));
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(updateQBooksFormWithContext());
    }, [dispatch, qbooksContext]);

    const showEmailToSupplier = isEmailToVendorEnabled || isEmailToVendorReadonly;

    const {
        isBelgradeUIFilesUploadSection,
        isBelgradeUIEmailToSupplier,
        isBelgradeUIShippingDetailsSection,
        isBelgradeUILineItemsTotalSection,
        isBelgradeUIQBooksAccountLineItemsSection,
    } = useUserBetaFeatures();

    const onChangeMemo = useCallback(
        (value: string) => {
            dispatch(changeQbooksMemo(value));
        },
        [dispatch]
    );

    return (
        <ConditionWrapper
            condition={
                !isBelgradeUIFilesUploadSection && !isBelgradeUIEmailToSupplier && !isBelgradeUIShippingDetailsSection
            }
            wrapperIf={(children) => (
                <FileDropProvider
                    className={className}
                    onUpload={onUploadAttachments}
                    maxSize={context.attachmentMaxSize}
                    allowedFileExtensions={qBooksConstants.QBO_ALLOWED_FILE_EXTENSIONS}
                >
                    {children}

                    {showDropArea && <SendToSupplierFileDropArea isSupplierAreaActive={sendToSupplier} />}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarHeader>
                        <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>

                        <ActionsBlock />
                    </ToolbarHeader>
                </Toolbar>

                <Content>
                    <InnerContent>
                        <RequesterInstructionSection instructionText={context.requesterInstruction} />

                        <QBooksVendorSection />

                        {isBelgradeUIShippingDetailsSection ? (
                            <PurchaseOrderShippingDetailsSection />
                        ) : (
                            <QBooksShippingSection />
                        )}

                        {isAccountBasedLinesEnabled &&
                            (isBelgradeUIQBooksAccountLineItemsSection ? (
                                <QBooksAccountLineItemsSection />
                            ) : (
                                <QBooksAccountLineItemsSectionLegacy />
                            ))}

                        {isItemBasedLinesEnabled && <QBooksLineItemsSection />}

                        {isBelgradeUILineItemsTotalSection ? <QBooksRequestItemsSummary /> : <QBooksFooterLegacy />}

                        {isBelgradeUIFilesUploadSection && (
                            <Grid gridTemplateColumns='50%' gap={16}>
                                <AttachmentsSection
                                    request={request}
                                    extensions={qBooksConstants.qboAllowedFileExtensions}
                                />
                            </Grid>
                        )}

                        <ConditionWrapper
                            condition={!isBelgradeUIVendorMessageSection}
                            wrapperIf={(children) => <Box spacing='28 60 0'>{children}</Box>}
                        >
                            <Flex justifyContent='space-between' wrap={false}>
                                <QBooksVendorMessageSection noPadding />

                                <MemoSection
                                    noPadding
                                    defaultValue={request.details?.memo || ''}
                                    disabled={ocrIsLoading}
                                    maxLength={constants.qBooksConstants.MEMO_MAX_LENGTH}
                                    onChange={onChangeMemo}
                                />
                            </Flex>
                        </ConditionWrapper>

                        {!isBelgradeUIFilesUploadSection && <FilesSection />}

                        {showEmailToSupplier && (
                            <QBooksEmailToVendorSection
                                request={request}
                                placeholderList={placeholderList}
                                isReadonly={isEmailToVendorReadonly}
                            />
                        )}
                    </InnerContent>
                </Content>
            </Root>
        </ConditionWrapper>
    );
};

export default memo(QBooksPurchaseOrderForm);
